/**
 * "Fees" modal (OR-1339 + OR-1494).
 *
 * One field does both jobs the design draws for it ("Select or type fee"):
 *   • pick one of the three predefined extra fees (MCare Fee, Indiana /
 *     Louisiana PCF Surcharge — OR-1339). Those persist on the quote's own
 *     fixed columns via the page's inputs;
 *   • or type any other fee name (OR-1494). Those are misc fees, created on
 *     `/quote/misc-fees/` by the page and shown as their own rows.
 *
 * Which path a row takes is decided from its text: an exact (case-
 * insensitive) match with a predefined label IS that predefined fee, anything
 * else is a custom fee. The suggestion list under the input mirrors the
 * brokerage "Type or Select" interaction (`AgentModal`): it offers whatever
 * predefined fees aren't already on the card or picked in another row, and
 * narrows as the user types.
 *
 * Mirrors `OnboardingContactModal`'s shape (ModalOverlay → Modal → Dialog
 * render-prop → form body with header/close-X + Cancel/Save footer). Every
 * open starts from a single blank row per the design — React Aria unmounts
 * the Dialog's content on close, so a fresh mount gives that for free.
 */
import { useRef, useState } from "react";
import { Trash02, X } from "@untitledui/icons";
import { Button, ButtonUtility, Dialog, Input, Modal, ModalOverlay } from "@tricura/atlas-ui";
import { EXTRA_FEE_TYPES, type ExtraFeeId, resolveExtraFeeId } from "../../../../domain/pricing";
import { CustomFeeSaveError } from "../../../../types/misc-fee";
import { MoneyInput } from "../../../../components/money-input";

export interface AddFeeModalResult {
    /** Predefined fees, one per catalog id — written to the quote inputs. */
    predefined: { id: ExtraFeeId; amount: string }[];
    /** Free-text fees — created as misc fees (OR-1494). */
    custom: { name: string; amount: number }[];
}

interface AddFeeModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** Predefined fee ids already added on the card — excluded from every row's suggestions. */
    existingFeeIds: ExtraFeeId[];
    /**
     * Fires once with every fully-filled row when the user saves. The modal
     * awaits it and only closes once it resolves; a rejection keeps the modal
     * open with the error shown. Reject with `CustomFeeSaveError` to keep just
     * the rows that failed on screen for a retry.
     */
    onSave: (result: AddFeeModalResult) => void | Promise<void>;
}

export function AddFeeModal({ isOpen, onOpenChange, existingFeeIds, onSave }: AddFeeModalProps) {
    // Lives on the overlay, not the body: while `onSave` is in flight no close
    // request may land — not the X, not Cancel, not Escape, not a click on the
    // backdrop — or a later `CustomFeeSaveError` would have no modal left to
    // show its error and retry rows in. The guard reads a ref, not the state,
    // so the body can clear it synchronously right before its own success
    // `close()` — a render-time closure would still see `saving === true` there
    // and swallow that close too.
    const savingRef = useRef(false);
    const [saving, setSavingState] = useState(false);
    const setSaving = (next: boolean) => {
        savingRef.current = next;
        setSavingState(next);
    };
    return (
        <ModalOverlay
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open && savingRef.current) return;
                onOpenChange(open);
            }}
            isKeyboardDismissDisabled={saving}
        >
            <Modal>
                <Dialog>
                    {({ close }) => (
                        <AddFeeModalBody existingFeeIds={existingFeeIds} close={close} onSave={onSave} saving={saving} setSaving={setSaving} />
                    )}
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

interface Row {
    key: number;
    /** What the underwriter typed or picked — a predefined label or a custom name. */
    text: string;
    amount: string;
    /** Set only after a blocked Save, so a fresh open/edit doesn't flash red. */
    error?: "feeType" | "amount";
}

let nextRowKey = 0;
function emptyRow(): Row {
    return { key: nextRowKey++, text: "", amount: "" };
}

function parseAmount(raw: string): number {
    const n = Number(raw.replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

interface BodyProps {
    existingFeeIds: ExtraFeeId[];
    close: () => void;
    onSave: (result: AddFeeModalResult) => void | Promise<void>;
    /** Owned by the overlay so it can refuse dismissal mid-save. */
    saving: boolean;
    setSaving: (saving: boolean) => void;
}

function AddFeeModalBody({ existingFeeIds, close, onSave, saving, setSaving }: BodyProps) {
    const [rows, setRows] = useState<Row[]>([emptyRow()]);
    const [serverError, setServerError] = useState<string | null>(null);
    /** Row whose suggestion list is open (the focused fee input). */
    const [openKey, setOpenKey] = useState<number | null>(null);

    const usedElsewhere = (rowKey: number) =>
        new Set<ExtraFeeId>([
            ...existingFeeIds,
            ...rows.filter((r) => r.key !== rowKey).map((r) => resolveExtraFeeId(r.text)).filter((id): id is ExtraFeeId => id !== null),
        ]);

    // Predefined fees still available to this row, narrowed by what's typed —
    // a row that already names one exactly keeps it listed (and checked).
    const suggestionsFor = (row: Row) => {
        const used = usedElsewhere(row.key);
        const needle = row.text.trim().toLowerCase();
        return EXTRA_FEE_TYPES.filter((t) => !used.has(t.id)).filter((t) => needle === "" || t.label.toLowerCase().includes(needle));
    };

    const updateRow = (key: number, patch: Partial<Row>) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch, error: undefined } : r)));
    };

    const removeRow = (key: number) => {
        setRows((prev) => {
            if (prev.length <= 1) return [emptyRow()];
            return prev.filter((r) => r.key !== key);
        });
    };

    const addRow = () => setRows((prev) => [...prev, emptyRow()]);

    const handleSave = async () => {
        const withErrors = rows.map((r) => {
            const hasFee = r.text.trim() !== "";
            const hasAmount = r.amount.trim() !== "";
            if (hasFee === hasAmount) return { ...r, error: undefined };
            return { ...r, error: hasFee ? ("amount" as const) : ("feeType" as const) };
        });
        if (withErrors.some((r) => r.error)) {
            setRows(withErrors);
            return;
        }
        const filled = rows.filter((r) => r.text.trim() !== "" && r.amount.trim() !== "");
        const result: AddFeeModalResult = { predefined: [], custom: [] };
        for (const r of filled) {
            const id = resolveExtraFeeId(r.text);
            if (id) result.predefined.push({ id, amount: r.amount });
            else result.custom.push({ name: r.text.trim(), amount: parseAmount(r.amount) });
        }
        setSaving(true);
        setServerError(null);
        try {
            await onSave(result);
            // Success: lift the dismissal guard first, then close.
            setSaving(false);
            close();
        } catch (error) {
            if (error instanceof CustomFeeSaveError) {
                // Predefined rows and the custom rows that did save are already on
                // the card — keep only the failed custom rows so a retry doesn't
                // create the successful ones twice.
                const failed = new Set(error.failed.map((f) => `${f.name.toLowerCase()}|${f.amount}`));
                setRows((prev) => {
                    const kept = prev.filter(
                        (r) => resolveExtraFeeId(r.text) === null && failed.has(`${r.text.trim().toLowerCase()}|${parseAmount(r.amount)}`),
                    );
                    return kept.length > 0 ? kept : [emptyRow()];
                });
            }
            setServerError(error instanceof Error ? error.message : "Failed to save fees");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-primary ring-primary flex w-[640px] flex-col rounded-xl ring-1" data-autotest="premium-selection-add-fee-modal">
            <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
                <div>
                    <h3 className="text-primary text-lg font-semibold">Fees</h3>
                    <p className="text-tertiary mt-1 text-sm">Add any fees that apply. Each amount adds to the final premium</p>
                </div>
                <ButtonUtility color="tertiary" size="sm" icon={X} onClick={close} isDisabled={saving} tooltip="Close" data-autotest="premium-selection-add-fee-modal-close-button" />
            </header>

            <div className="flex flex-col gap-3 px-6">
                {/* Column headers for the whole row list — the fields themselves
                  * carry no labels, so these are the table's only headings. */}
                <div className="flex items-center gap-4">
                    <span className="text-secondary w-[380px] shrink-0 text-sm font-medium">Fee</span>
                    <span className="text-secondary w-[140px] shrink-0 text-sm font-medium">Amount</span>
                    <span className="w-9 shrink-0" aria-hidden="true" />
                </div>

                {rows.map((row) => {
                    const isEmpty = row.text.trim() === "" && row.amount.trim() === "";
                    const suggestions = suggestionsFor(row);
                    const selectedId = resolveExtraFeeId(row.text);
                    return (
                        <div key={row.key} className="flex items-start gap-4" data-autotest="premium-selection-add-fee-modal-row">
                            <div className="relative w-[380px] shrink-0">
                                {/* No visible label (the column header is it), so the
                                  * placeholder doubles as the accessible name. */}
                                <Input
                                    size="md"
                                    placeholder="Select or type fee"
                                    value={row.text}
                                    isDisabled={saving}
                                    isInvalid={row.error === "feeType"}
                                    hint={row.error === "feeType" ? "Select a fee" : undefined}
                                    onChange={(v) => updateRow(row.key, { text: v })}
                                    onFocus={() => setOpenKey(row.key)}
                                    // Delay the hide so a click on a suggestion fires
                                    // its onClick before the list unmounts.
                                    onBlur={() => setTimeout(() => setOpenKey((k) => (k === row.key ? null : k)), 150)}
                                    data-autotest="premium-selection-add-fee-modal-fee-input"
                                />
                                {openKey === row.key && suggestions.length > 0 && (
                                    <ul
                                        role="listbox"
                                        aria-label="Predefined fees"
                                        className="bg-primary ring-secondary_alt absolute z-10 mt-1 w-full overflow-auto rounded-lg py-1 shadow-lg ring-1 outline-none"
                                    >
                                        {suggestions.map((t) => (
                                            <li key={t.id}>
                                                {/* The option element itself takes the click, so the
                                                  * role and the handler always land on the same node. */}
                                                <button
                                                    type="button"
                                                    role="option"
                                                    aria-selected={selectedId === t.id}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => {
                                                        updateRow(row.key, { text: t.label });
                                                        setOpenKey(null);
                                                    }}
                                                    data-autotest="premium-selection-add-fee-modal-fee-type-option"
                                                    className="text-primary hover:bg-primary_hover block w-full cursor-pointer px-3 py-2 text-left text-sm font-medium transition duration-100 ease-linear"
                                                >
                                                    {t.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="w-[140px] shrink-0">
                                <MoneyInput
                                    aria-label="Amount"
                                    size="md"
                                    value={row.amount}
                                    isDisabled={saving}
                                    isInvalid={row.error === "amount"}
                                    onChange={(raw) => updateRow(row.key, { amount: raw })}
                                    data-autotest="premium-selection-add-fee-modal-amount-input"
                                />
                                {row.error === "amount" && <p className="text-error-primary mt-1.5 text-xs">Enter an amount</p>}
                            </div>
                            {/* The button is 32px tall against a 40px input box, so
                              * `h-10 items-center` puts it on the input's own centre line.
                              * Pinned to the top of the row, so a validation hint appearing
                              * under either field can't drag it out of alignment. */}
                            <div className="flex h-10 shrink-0 items-center">
                                <ButtonUtility
                                    color="secondary"
                                    size="sm"
                                    icon={Trash02}
                                    tooltip="Delete row"
                                    isDisabled={isEmpty || saving}
                                    onClick={() => removeRow(row.key)}
                                    data-autotest="premium-selection-add-fee-modal-delete-row-button"
                                />
                            </div>
                        </div>
                    );
                })}

                {/* Always available (except mid-save, when every row control is
                  * frozen so a failure reconciles against what was submitted): a
                  * new row can hold a custom fee even once every predefined type
                  * has been used. */}
                <Button color="link-gray" size="sm" className="self-start" onClick={addRow} isDisabled={saving} data-autotest="premium-selection-add-fee-modal-add-fee-button">
                    + Add Fee
                </Button>
                {serverError && (
                    <p className="text-error-primary text-sm" role="alert" data-autotest="premium-selection-add-fee-modal-error">
                        {serverError}
                    </p>
                )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-6">
                <Button color="secondary" size="sm" onClick={close} isDisabled={saving} data-autotest="premium-selection-add-fee-modal-cancel-button">
                    Cancel
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    onClick={() => void handleSave()}
                    isLoading={saving}
                    showTextWhileLoading
                    data-autotest="premium-selection-add-fee-modal-save-button"
                >
                    Save
                </Button>
            </div>
        </div>
    );
}
